import React, {
  useRef,
  useEffect,
  useState,
  forwardRef,
  useMemo,
  useImperativeHandle,
} from "react";
import { AgGridReact } from "ag-grid-react";
import "ag-grid-community/styles/ag-theme-alpine.css";
import "ag-grid-enterprise";
import "@shared/components/Grid/SnGrid.css"; // 커스텀 CSS 추가

import {
  ModuleRegistry,
  ExcelExportModule,
  RowGroupingModule,
  ClientSideRowModelModule,
  GridChartsModule,
  ColDef,
  CellFocusedEvent,
} from "ag-grid-enterprise";
import { global, useMenuStore } from "@shared/libs/Global";
import SnTab from "@shared/components/SnTab";
import { StyleSettings } from "../../../styles/StyleSettings";

import SnTabHeader from "@shared/components/SnTabHeader";

import CustomHeaderCheckbox from "@shared/components/Grid/CustomHeaderCheckbox";
import { createColumnDefs } from "@shared/components/Grid/SnGrid_ColumnDefs";
import DrawingButtons from "./DesignerButtons";
import { IHeaderParams } from "ag-grid-community";
import HighlightOffIcon from "@mui/icons-material/HighlightOff";
import SnButton from "@shared/components/SnButton";
import { CustomColDef } from "@shared/components/Grid/SnGrid_Types";
import { getTablebySql } from "@shared/utils/CommonUtils";
import TemplatePopup1 from "./Templates/TemplatePopup1";
import { showResizablePopupModal } from "@widgets/CommonModal";

// 필요한 모듈 등록
ModuleRegistry.registerModules([
  ClientSideRowModelModule,
  ExcelExportModule,
  RowGroupingModule,
  GridChartsModule,
]);

//#endregion
interface DrawingSnGridProps {
  seq: number;
  columnDefs?: ColDef[]; // 직접 전달된 컬럼 정의
  columnConfig?: any[]; // 컬럼 설정을 기반으로 생성할 경우
  rowData: any[] | null; // null 허용
  gridOptions?: any; // AG Grid 옵션
  enableAddRow?: boolean; // 행 추가 활성화 여부
  ischeckboxSelection?: boolean; // 체크박스 셀렉션
  title?: string;
  onBtnAddClick?: (id: string) => void;
  onBtnDeleteClick?: (id: string) => void;
  header?: React.ReactNode;
  menuOptions?: any;
  settingsData?: any;
  isPopup?: boolean;
}

const DrawingSnGrid = forwardRef((props: DrawingSnGridProps, ref) => {
  // 구조 분해를 통해 props에서 필요한 속성 추출
  const {
    seq,
    columnDefs, // 직접 전달된 columnDefs
    columnConfig, // columnConfig를 기반으로 columnDefs 생성
    gridOptions = {},
    title,
    onBtnAddClick,
    onBtnDeleteClick,
    header,
    ischeckboxSelection = false, // 체크박스 선택 기능 여부
    menuOptions,
    settingsData,
    isPopup,
  } = props;
  const [errorMap, setErrorMap] = useState(new Map()); // 에러메시지
  const [columns, setColumns] = useState<any[]>([{}]);
  const [requireds, setRequireds] = useState<any>({}); /* */
  const [editCons, setEditCons] = useState<any>({}); /* */
  const [popupSettings, setPopupSettings] = useState<any>(); /* */
  // 전역 변수 선언

  const cdGrid = `grid${seq}`;

  const [settings, setSettings] = useState({
    title: "",
    table: "",
    defaultValue: "",
    button: "",
    defaultValueObject: null,
    buttonNode: null,
    columnsInfo: null,
  });

  //#endregion
  const { userInfo } = global();
  // State로 columnDefs 관리
  const [resolvedColumnDefs, setResolvedColumnDefs] = useState<ColDef[]>([]);

  // editable 여부를 제어하는 함수
  const falseFunc = (field: string): boolean => {
    return false && field !== "readonly_field";
  };

  // 이전 포커스된 행 데이터 저장용 Ref
  const isProcessingRef = useRef<boolean>(false); // 엔터 두번으로 팝업이 두번 오픈됨을 막는
  // columnConfig 변경 시에만 createColumnDefs 실행
  useEffect(() => {
    setColumns(Array.isArray(columnConfig) ? columnConfig : []);
  }, [columnConfig]);

  useEffect(() => {
    setColumnDefsWithRowNumber([rowNumberColumnDef, ...resolvedColumnDefs]);
  }, [resolvedColumnDefs]);

  useEffect(() => {
    if (settingsData) {
      let button;
      if (settingsData.button) {
        button = convertButton(settingsData.button); // 버튼 변환
      }
      setSettings({
        ...settingsData,
        buttonNode: button,
      });
    }
  }, [settingsData]);

  const memoizedColumnConfig = useMemo<any[]>(() => {
    return Array.isArray(columnConfig) ? columnConfig : [];
  }, [columnConfig]);

  useEffect(() => {
    const loadColumnDefs = async () => {
      let updatedColumnDefs:
        | any[]
        | ((prevState: ColDef<any, any>[]) => ColDef<any, any>[]) = [];

      if (columnDefs) {
        // columnDefs가 직접 전달된 경우 그대로 설정
        updatedColumnDefs = columnDefs;
      } else if (memoizedColumnConfig) {
        // columnConfig가 변경된 경우에만 비동기 함수 호출

        // memoizedColumnConfig의 첫 번째 항목에서 cd_grid 추출
        // cdGrid = memoizedColumnConfig[0]?.cd_grid || null;

        const requiredsFilter = memoizedColumnConfig.filter(
          (col) => col.yn_required == "Y"
        );
        let tempreq = Object.assign({});
        requiredsFilter.map((req) => {
          tempreq[req.cd_column] = req.nm_column;
        });
        setRequireds(tempreq);

        const editConsFilter = memoizedColumnConfig.filter(
          (col) => col.dc_editcon != "" && col.dc_editcon != null
        );
        let tempeditCons = Object.assign({});
        editConsFilter.map((con) => {
          tempeditCons[con.cd_column] = con.dc_editcon;
        });
        setEditCons(tempeditCons);

        const generatedColumnDefs = await createColumnDefs(
          memoizedColumnConfig,
          falseFunc,
          userInfo,
          errorMap
          // ,          gridRefcol
        );
        // updatedColumnDefs = generatedColumnDefs;
        updatedColumnDefs = generatedColumnDefs.map((colDef) => ({
          headerComponent: CustomHeader,
          ...colDef,
        }));
      }
      // checkboxSelection이 필요한 경우 컬럼 추가
      if (ischeckboxSelection) {
        updatedColumnDefs = [
          {
            headerName: "",
            cellRendererSelector: (params: any) => {
              if (params.node?.rowPinned) {
                return { component: "" }; // footer는 아무것도 안 보여줌
              }
              return { component: "agCheckboxCellRenderer" };
            },
            cellEditor: "agCheckboxCellEditor",
            width: 20,
            colId: "fixCheckbox",
            field: "fixCheckbox",
            headerComponent: CustomHeaderCheckbox,
            editable: true,
            cellStyle: {
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            },
          },
          ...updatedColumnDefs,
        ];
      }
      setResolvedColumnDefs(updatedColumnDefs);
    };

    loadColumnDefs();
  }, [columnDefs, memoizedColumnConfig]);

  const defaultColDef: ColDef = useMemo(
    () => ({
      sortable: true,
      filter: "agTextColumnFilter", // 헤더 필터 비활성화 (타입을 명시해야 정상 작동됨.)
      floatingFilter: false, // 플로팅 필터 비활성화
      resizable: true,
      suppressMenu: true,
      enableRowGroup: true,
      editable: false, // 수정 가능 여부를 props로 제어
      headerClass: "header-center",
    }),
    []
  );

  //#endregion

  //#region 초기화

  const gridRef = useRef<AgGridReact>(null); // AG Grid 내부 참조

  const gridApiRef = useRef<any>(null); // AG Grid API
  const columnApiRef = useRef<any>(null); // AG Grid Column API

  //#endregion

  //#region 필터 관련 내용
  const rowNumberColumnDef = {
    headerName: "No.", // 순번 컬럼
    valueGetter: (params: { node: { rowPinned: any; rowIndex: number } }) => {
      if (params.node.rowPinned) {
        const visibleRowCount =
          gridRef.current?.api.getDisplayedRowCount() || 0;
        return visibleRowCount;
      }
      return params.node.rowIndex + 1; // 일반 행에서는 순번 표시
    },
    colId: "fixIndicator",
    width: 70,
    pinned: "left",
    cellClass: "row-number-cell",
    suppressMovable: true,
    suppressNavigable: true,
    sortable: false,
    filter: false,
  };
  //#endregion

  //#region Indicator 관련 내용
  const [columnDefsWithRowNumber, setColumnDefsWithRowNumber] = useState([
    rowNumberColumnDef,
    ...resolvedColumnDefs, // 기존 컬럼 정의
  ]);

  //#endregion

  //#region 외부에서 접근 가능한 메서드 정의
  useImperativeHandle(ref, () => ({
    getColumns: () => {
      return columns.map((c: any) => ({
        ...c,
        cd_grid: cdGrid,
      }));
    },
    getSettings: async () => {
      let defaultValue: any = {};
      if (columns.length === 1 && Object.keys(columns[0]).length === 0)
        return { ...settings, defaultValue: {} };
      const filtered = columns.filter(
        (col) =>
          col.defaultValue !== undefined &&
          col.defaultValue !== null &&
          col.defaultValue !== ""
      );
      filtered.map((col) => {
        if (col.tp_column === "SnNumberBox")
          defaultValue[col.cd_column] = col.defaultValue;
        else defaultValue[col.cd_column] = `'${col.defaultValue}'`;
      });
      const filteredDt = columns.filter((col) => col.cd_column.includes("dt_"));
      filteredDt.map((col) => {
        if (
          col.defaultValue !== undefined &&
          col.defaultValue !== null &&
          col.defaultValue !== ""
        )
          defaultValue[col.cd_column] =
            `getConvertedDate('${col.defaultValue}')`;
        else defaultValue[col.cd_column] = `getConvertedDate('0/0/0')`;
      });

      let columnsInfo;
      if (settings.columnsInfo == null) {
        columnsInfo = await getColumnsInfo(settings.table);
      } else {
        columnsInfo = settings.columnsInfo;
      }

      columnsInfo?.map((col) => {
        Object.keys(userInfo).forEach((key) => {
          if (col.column_name.includes(key) && key !== "cd_cust") {
            defaultValue[col.column_name] = `userInfo.${key}`;
            if (key in ["cd_emp", "cd_dept", "cd_gp", "cd_user"]) {
              defaultValue[col.column_name.replace("cd", "nm")] =
                `userInfo.${key.replace("cd", "nm")}`;
            }
          }
        });
      });
      return {
        ...settings,
        defaultValue: defaultValue,
        columnsInfo: columnsInfo,
      };
    },
    getPopupSettings: async () => {
      (() => {
        if (!popupSettings) return {};
        const newObj: any = {};
        Object.values(popupSettings).forEach((value, idx) => {
          newObj[(idx + 1).toString()] = value;
        });
        return newObj;
      })();
    },
  }));

  const getColumnsInfo = async (tablename: string) => {
    try {
      const rtn: any[] = await getTablebySql({
        sql: `SELECT
                  col.column_name,
                  col.data_type,
                  col.character_maximum_length,
                  col.numeric_precision,
                  col.numeric_scale,
                  col.is_nullable,
                  col.column_default,
                  CASE WHEN pk.column_name IS NOT NULL THEN 'YES' ELSE 'NO' END AS is_primary_key
              FROM information_schema.columns col
              LEFT JOIN (
                  SELECT 
                      kcu.column_name
                  FROM 
                      information_schema.table_constraints tc
                  JOIN information_schema.key_column_usage kcu 
                      ON tc.constraint_name = kcu.constraint_name
                      AND tc.table_name = kcu.table_name
                  WHERE tc.constraint_type = 'PRIMARY KEY'
                    AND tc.table_name = '${tablename}'
                    AND tc.table_schema = 'snotes'
              ) pk
                  ON col.column_name = pk.column_name
              WHERE col.table_name = '${tablename}'
                AND col.table_schema = 'snotes'
              ORDER BY col.ordinal_position;`,
      });
      return rtn;
    } catch (error) {}
  };
  //#endregion

  const onGridSetting = (value: any) => {
    setSettings(value);

    let button: any;
    if (value.button) {
      button = convertButton(value.button);
    }

    setSettings((prev) => ({
      ...prev,
      ...value,
      buttonNode: button,
    }));
  };

  const onGridBtnClick = async (key: string) => {
    if (key.includes("적용")) {
      const result = await showResizablePopupModal({
        title: `적용`,
        children: <TemplatePopup1 loadData={null} />,
      });
      if (!result || result === "close") return;
      setPopupSettings((prev: any = {}) => ({
        ...prev,
        [key]: result[0],
      }));
    } else {
      alert(key);
    }
  };

  const convertButton = (value: string) => {
    const buttonLabels = value
      .split(",")
      .map((label) => label.trim())
      .filter((label) => label);

    const nodes = buttonLabels.map((label) => (
      <SnButton
        key={label}
        text={label}
        onClick={() => onGridBtnClick(label)}
      />
    ));
    return nodes;
  };

  let debounceTimeout: number;

  const debounce = (func: (...args: any[]) => void, delay: number) => {
    return (...args: any[]) => {
      clearTimeout(debounceTimeout); // timeout을 해제
      debounceTimeout = window.setTimeout(() => func(...args), delay); // setTimeout 사용
    };
  };

  const handleColumnStateChange = debounce(() => {
    if (gridRef.current?.api) {
      // 컬럼 상태 가져오기
      const columnState = gridRef.current.api.getColumnState();

      // resolvedColumnDefs 업데이트
      const updatedColumnDefs: CustomColDef[] = columnState
        .map((state) => {
          const matchingColDef = resolvedColumnDefs.find(
            (colDef) =>
              colDef.field === state.colId || colDef.colId === state.colId
          );

          if (!matchingColDef) return null;

          return {
            ...matchingColDef,
            hide: state.hide ?? false, // hide 처리
            pinned: state.pinned ?? null, // 고정 상태
            width: state.width ?? matchingColDef.width, // 기존 너비 유지
            initialWidth: state.width ?? matchingColDef.width, // 기존 너비 유지
          } as CustomColDef; // 명시적으로 ColDef 단언
        })
        .filter((colDef): colDef is CustomColDef => colDef !== null); // null 제거 및 타입 단언

      setResolvedColumnDefs(updatedColumnDefs);
      setColumns((prev) => {
        // columnState 기준으로 새 배열 생성
        const reordered = columnState
          .map((state, idx) => {
            // prev에서 같은 cd_column을 가진 항목 찾기
            const match = prev.find((col) => col.cd_column === state.colId);

            if (!match) return null; // 일치하는 게 없으면 제외

            return {
              ...match,
              no_seq: idx, // 순서 재조정
              qt_width: state.width ?? match.qt_width, // width 반영
            };
          })
          .filter((col): col is (typeof prev)[number] => col !== null); // null 제거
        return reordered;
      });
    }
  }, 300);

  const recreateColumnDefs = async (columns: any[]) => {
    const generatedColumnDefs = await createColumnDefs(
      columns,
      falseFunc,
      userInfo,
      errorMap
      // ,          gridRefcol
    );
    // updatedColumnDefs = generatedColumnDefs;
    const updatedColumnDefs = generatedColumnDefs.map((colDef) => ({
      headerComponent: CustomHeader,
      ...colDef,
    }));
    setResolvedColumnDefs(updatedColumnDefs);
    setColumnDefsWithRowNumber([rowNumberColumnDef, ...updatedColumnDefs]);
  };

  const renderGridContent = () => {
    if (!columnConfig || columnConfig.length === 0) {
      return;
    }
    return (
      <div
        className="DrawingSnGrid"
        style={{
          display: "flex",
          flexDirection: "column",
          height: "100%",
          width: "100%",
        }}
      >
        {/* 그리드 */}
        <div className="ag-theme-alpine" style={{ flex: "1 1 auto" }}>
          <AgGridReact
            rowStyle={{ fontSize: StyleSettings.font }}
            containerStyle={{ fontSize: StyleSettings.font }}
            ref={gridRef}
            columnDefs={columnDefsWithRowNumber}
            rowData={[]}
            // pinnedBottomRowData={pinnedBottomRowData}
            defaultColDef={defaultColDef}
            headerHeight={30} // header 높이 설정
            getRowHeight={() => {
              return 30;
            }} // cell, footer 높이 설정
            // stopEditingWhenCellsLoseFocus={true} // grid 외부 클릭 시 editing 비활성화

            {...gridOptions}
            groupDefaultExpanded={-1} // 그룹화할 때 전체확장: -1, 전체축소: 0
            // statusBar={statusBar} // 드래그 했을 때 sum 나오는거 일단 추가
            suppressCellFlash={true} // 데이터 변경 시 자연스러움 추가
            suppressHeaderKeyboardEvent={true}
            suppressContextMenu={true} // 기본 context 메뉴 표시 안 함
            onColumnMoved={handleColumnStateChange}
            onColumnResized={handleColumnStateChange}
            onColumnPinned={handleColumnStateChange}
            onColumnVisible={handleColumnStateChange}
          />
        </div>
        <div className="relative">
          <DrawingButtons
            type="그리드"
            data={columns}
            onAdd={(value: any[]) => {
              setColumns((prev) => {
                const merged = (
                  prev.length === 1 && Object.keys(prev[0]).length === 0
                    ? []
                    : prev
                ).concat(value[0]);
                const sorted = merged.map((col, idx) => ({
                  ...col,
                  no_seq: idx + 1,
                })); // no_seq 재조정

                recreateColumnDefs(sorted);
                return sorted;
              });
            }}
            onChange={(value: any[]) => {
              setColumns(value);

              recreateColumnDefs(value);
            }}
            onGridSetting={onGridSetting}
            settings={settings}
          />
        </div>
      </div>
    );
  };

  return (
    <>
      {title ? (
        <SnTab
          titles={[settings.title == "" ? title : settings.title]}
          width={"100%"}
          header={[
            <SnTabHeader
              onBtnAddClick={onBtnAddClick}
              onBtnDeleteClick={onBtnDeleteClick}
              body={settings.buttonNode || header}
            />,
          ]}
          body={[renderGridContent()]}
        />
      ) : (
        renderGridContent()
      )}
    </>
  );
});
export default React.memo(DrawingSnGrid);

const CustomHeader: React.FC<IHeaderParams> = (props) => {
  const { displayName, column, api } = props;
  const [hover, setHover] = useState(false);

  const hideColumn = () => {
    //api.setColumnVisible(column.getColId(), false);
  };

  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center", // 텍스트는 가운데
        width: "100%",
        height: "100%",
        position: "relative", // X 버튼 절대 위치
        boxSizing: "border-box",
      }}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
    >
      {/* 가운데 정렬 텍스트 */}
      <span
        style={{
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }}
      >
        {displayName}
      </span>

      {/* X 버튼 */}
      <span
        style={{
          position: "absolute",
          top: "50%",
          right: 0, // 완전 우측
          transform: "translateY(-50%)", // 세로 가운데 정렬
          cursor: "pointer",
          zIndex: 10,
          background: "white",
          borderRadius: "50%",
          boxShadow: "0 1px 4px rgba(0,0,0,0.08)",
          padding: 2,
          display: "flex",
          alignItems: "center",
          justifyContent: "center", // 아이콘 가운데 배치
          opacity: hover ? 1 : 0, // hover 시 표시
          transition: "opacity 0.2s ease",
        }}
        onClick={hideColumn}
      >
        <HighlightOffIcon fontSize="small" color="error" />
      </span>
    </div>
  );
};
